import { useEffect, useState } from "react";

import Clarity from "@microsoft/clarity";
import { jwtDecode } from "jwt-decode";
import { Col, Container, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useAppDispatch } from "../../../hooks";
import { LexIpAuthTokenPayload } from "../../../models";
import { confirmUserCode, requestUserCode } from "../../../services";
import { loginWithToken } from "../../../store/reducer/_sessionReducer";
import { getLoggedIn } from "../../../store/selectors";
import { getLoggedUser } from "../../../utils";
import { Pagewrapper } from "../../components";
import { PDLTypography } from "../../components/dna";
import { Copyright, Logo } from "../../components/global";
import RegisterForm from "../../components/global/_registerForm";
import VerifyCodeForm from "../../components/global/_virifyCodeForm";
import "./style.scss";

type Step = "register" | "verify";

const RegisterDependentPage = (props: any) => {
  const [step, setStep] = useState<Step>("register");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const loggedUser = useSelector(getLoggedUser);
  const loggedIn = useSelector(getLoggedIn);

  const isIpAccount = loggedIn && loggedUser && loggedUser.origem === "ip_auth";

  const handleRegisterSubmit = async (
    userEmail: string,
    userPassword: string,
    userName: string,
  ) => {
    try {
      setIsLoading(true);

      await requestUserCode({ email: userEmail });

      setEmail(userEmail);
      setPassword(userPassword);
      setName(userName);
      setError("");
      setStep("verify");
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.message || "Erro ao enviar código");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifySubmit = async (code: string) => {
    try {
      setIsLoading(true);

      const response = await confirmUserCode({
        email,
        code,
        password,
        name,
      });

      const { token } = response;

      const decoded = jwtDecode(token) as LexIpAuthTokenPayload;

      const user = {
        name: decoded.name,
        email: decoded.email,
        origem: decoded.type,
        idPessoa: decoded.accountId,
      };

      Clarity.identify(String(decoded.accountId));
      Clarity.setTag("user_email", decoded.email);
      Clarity.setTag("user_origin", decoded.type);

      dispatch(
        loginWithToken({
          user,
          token,
        }),
      );

      setError("");
      navigate("/home");
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.message || "Código inválido");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setIsLoading(true);

      await requestUserCode({ email });
      setError("");
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.message || "Erro ao reenviar código");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep("register");
  };

  useEffect(() => {
    if (!isIpAccount) {
      navigate("/home");
    }
  }, [isIpAccount]);

  return (
    <Pagewrapper {...props}>
      <Container fluid className="pdl_page_register">
        <Row className="h-100">
          <Col sm={6} className="h-100 pdl_page_register_column_left">
            <Container className="h-100 d-flex flex-column align-items-center justify-content-between">
              <Row></Row>
              <Row className="pdl_page_register_form_container">
                <Col>
                  <div className="form_container">
                    <Logo variant="login" className="mb-5" />

                    {step === "register" ? (
                      <RegisterForm
                        onSubmit={handleRegisterSubmit}
                        isLoading={isLoading}
                        error={error}
                      />
                    ) : (
                      <VerifyCodeForm
                        email={email}
                        errorMsg={error}
                        onSubmit={handleVerifySubmit}
                        onBack={handleBack}
                        onResend={handleResendCode}
                        isLoading={isLoading}
                      />
                    )}
                  </div>
                </Col>
              </Row>
              <Row className="w-100">
                <Col className="py-3 justify-self-stretch">
                  <Copyright />
                </Col>
              </Row>
            </Container>
          </Col>
          <Col
            sm={6}
            className="h-100 pdl_page_register_column_right d-none d-sm-block"
          >
            <Container className="h-100 p-0 m-0 d-flex flex-column align-items-center justify-content-center">
              <Row>
                <Col className="pdl_page_register_column_right_contents p-0 m-0 text-center d-grid gap-5">
                  <img
                    alt="Plataforma LEX"
                    className="pdl_page_register_image_plataforma rounded"
                    src="/images/generic/plataforma_lex.png"
                  />
                  <div className="d-grid gap-2">
                    <PDLTypography size="xl" variant="light" weight="medium">
                      Crie sua conta na Plataforma LEX
                    </PDLTypography>
                    <PDLTypography size="md" variant="light" weight="medium">
                      Sua fonte central de pesquisa jurídica, com conteúdos
                      confiáveis e atualizados.
                    </PDLTypography>
                  </div>
                </Col>
              </Row>
            </Container>
          </Col>
        </Row>
      </Container>
    </Pagewrapper>
  );
};
export default RegisterDependentPage;
